import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'

export type QueryValue = string | string[] | number | boolean | undefined

export interface NextStyleQuery {
  [key: string]: QueryValue
}

type RouterUrlObject = {
  pathname?: string
  query?: Record<string, QueryValue | undefined>
}

function buildSearchFromQuery(query: Record<string, QueryValue | undefined>): string {
  const params = new URLSearchParams()
  for (const [key, val] of Object.entries(query)) {
    if (val === undefined) continue
    if (Array.isArray(val)) {
      val.forEach((v) => params.append(key, String(v)))
    } else {
      params.set(key, String(val))
    }
  }
  const s = params.toString()
  return s ? `?${s}` : ''
}

function mergeQueryFromSearchAndParams(
  searchParams: URLSearchParams,
  routeParams: Record<string, string | undefined>,
): NextStyleQuery {
  const q: NextStyleQuery = {}
  for (const [k, v] of Object.entries(routeParams)) {
    if (v !== undefined) q[k] = v
  }
  searchParams.forEach((value, key) => {
    const existing = q[key]
    if (existing === undefined) {
      q[key] = value
    } else if (Array.isArray(existing)) {
      existing.push(value)
    } else {
      q[key] = [String(existing), value]
    }
  })
  return q
}

export function useRouter() {
  const navigate = useNavigate()
  const location = useLocation()
  const routeParams = useParams()
  const [searchParams] = useSearchParams()

  const query = useMemo(
    () => mergeQueryFromSearchAndParams(searchParams, routeParams as Record<string, string | undefined>),
    [searchParams, routeParams],
  )

  const push = useCallback(
    (url: string | RouterUrlObject, _as?: unknown, _opts?: { shallow?: boolean }) => {
      if (typeof url === 'string') {
        navigate(url)
        return
      }
      const pathname = url.pathname ?? location.pathname
      const search = url.query !== undefined ? buildSearchFromQuery(url.query) : ''
      navigate(pathname + search)
    },
    [navigate, location.pathname],
  )

  const replace = useCallback(
    (url: string | RouterUrlObject, _as?: unknown, _opts?: { shallow?: boolean }) => {
      if (typeof url === 'string') {
        navigate(url, { replace: true })
        return
      }
      const pathname = url.pathname ?? location.pathname
      const search = url.query !== undefined ? buildSearchFromQuery(url.query) : ''
      navigate(pathname + search, { replace: true })
    },
    [navigate, location.pathname],
  )

  return {
    pathname: location.pathname,
    asPath: location.pathname + location.search,
    query,
    push,
    replace,
    isReady: true,
  }
}
